import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, Alert, CircularProgress
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import axiosInstance from '../../utils/axiosConfig';
import { trackEvent } from '../../utils/analytics';

interface Props {
  open: boolean;
  onClose: () => void;
  bandName?: string;
  bandId?: string;
}

const PromoterInterestModal: React.FC<Props> = ({ open, onClose, bandName, bandId }) => {
  const [form, setForm] = useState({ name: '', organisation: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.organisation || !form.email) return;
    setStatus('sending');
    try {
      await axiosInstance.post('/api/email/express-interest', {
        name: form.name,
        organisation: form.organisation,
        email: form.email,
        bandName: bandName || null,
        message: form.message || null,
      });
      trackEvent('interest_expressed', { bandId, bandName: bandName || undefined });
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or email us directly at hello@improvisedmusic.ie');
    }
  };

  const handleClose = () => {
    if (status === 'sending') return;
    setForm({ name: '', organisation: '', email: '', message: '' });
    setStatus('idle');
    setErrorMsg('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 0 }}>
        <HandshakeIcon color="primary" />
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {bandName ? `Interested in ${bandName}?` : 'Work with IMC'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tell us about yourself and we'll be in touch
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {status === 'success' ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Thank you!</Typography>
            <Typography color="text.secondary">
              We've received your interest{bandName ? ` in ${bandName}` : ''} and will be in touch shortly.
              IMC can put together a curated list of options that fits your programming needs.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {status === 'error' && <Alert severity="error">{errorMsg}</Alert>}

            <TextField
              name="name"
              label="Your name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
              disabled={status === 'sending'}
            />
            <TextField
              name="organisation"
              label="Organisation / Venue / Festival"
              value={form.organisation}
              onChange={handleChange}
              required
              fullWidth
              disabled={status === 'sending'}
              helperText="e.g. Ronnie Scott's, North Sea Jazz, Edinburgh Jazz Festival"
            />
            <TextField
              name="email"
              label="Your email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              disabled={status === 'sending'}
            />
            <TextField
              name="message"
              label="Tell us what you're looking for (optional)"
              value={form.message}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
              disabled={status === 'sending'}
              placeholder="e.g. A piano trio for a 200-cap venue, available in October..."
            />
            <Typography variant="caption" color="text.secondary">
              IMC represents artists not yet on this platform too — if you don't see quite
              what you need, we can find the right match for you.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={status === 'sending'}>
          {status === 'success' ? 'Close' : 'Cancel'}
        </Button>
        {status !== 'success' && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.name || !form.organisation || !form.email || status === 'sending'}
            startIcon={status === 'sending' ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {status === 'sending' ? 'Sending…' : 'Send Expression of Interest'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PromoterInterestModal;
